"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import TabbedContent from "@/components/common/TabbedContent";
import ScrollReveal from "../common/ScrollReveal";
import ClientOnly from "@/components/common/ClientOnly";
import { useIsDesktop } from "@/hooks/useMediaQuery";

type Section = "analysis" | "risk" | "exploit";

const AUTO_CYCLE_INTERVAL = 6000;
const PAUSE_DURATION = 10000;

export default function ScarfProduct() {
  const [activeTab, setActiveTab] = useState<Section>("analysis");
  const [hoveredSection, setHoveredSection] = useState<Section | null>(null);
  const [isCircleHovered, setIsCircleHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hoveredValueIndex, setHoveredValueIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const isDesktop = useIsDesktop();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const sectionKeys: Section[] = ["analysis", "risk", "exploit"];

  const coreCapabilities = [
    {
      id: "analysis",
      title: "Advanced Blackbox Analysis",
      content:
        "Gain visibility into complex systems through binary analysis and attack path discovery without requiring source code. By combining static inspection and dynamic runtime monitoring, the platform identifies dependencies across multiple formats and environments. It delivers a comprehensive view of potential attack surfaces and component interactions, helping organizations understand how vulnerabilities could be chained and exploited.",
    },
    {
      id: "risk",
      title: "Risk Management",
      content:
        "Ensure stronger security decisions with structured risk evaluation and continuous monitoring. The platform supports risk scoring, vendor assessment, and SBOM generation, while tracking license compliance across components. With real-time monitoring and trend analysis, teams can quickly detect new issues, prioritize remediation based on impact, and apply practical, layered defenses tailored to their environment.",
    },
    {
      id: "exploit",
      title: "Exploit Validation",
      content:
        "Validate security posture with evidence-based assessment of exploitability. The platform aggregates intelligence from multiple sources, correlates vulnerabilities with known exploits, and safely tests them in controlled environments. Automated validation and exploit chain modeling provide clear insight into which risks are actionable, helping security teams focus efforts where it matters most.",
    },
  ];

  const keyValueItems = [
    {
      title: "Beyond Individual Vulnerability Assessment",
      items: [
        "Comprehensive Risk Scoring: Evaluate entire supply chain risk, not just isolated vulnerabilities",
        "Attack Path Analysis: Maps complete threat landscapes and exploitation chains",
        "Practical Validation: Generates proof-of-concept demonstrations for real-world verification",
        "Evidence-Based Assessment: Provides concrete evidence of exploitability rather than theoretical scores",
      ],
    },
    {
      title: "Zero Source Code Dependency",
      items: [
        "Blackbox Analysis: Complete security assessment using only software installers",
        "No Code Access Required: Perfect for third-party software evaluation",
        "User Perspective Security: Analyzes software exactly as end-users would experience it",
        "Installation Behavior Analysis: Monitors actual deployment and runtime behavior",
      ],
    },
  ];

  // Get next section in cycle
  const getNextSection = useCallback((current: Section): Section => {
    const currentIndex = sectionKeys.indexOf(current);
    return sectionKeys[(currentIndex + 1) % sectionKeys.length];
  }, []);

  // Handle user click - select and pause
  const handleSectionClick = useCallback((section: Section) => {
    setActiveTab(section);
    setIsPaused(true);

    if (timerRef.current) clearInterval(timerRef.current);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);

    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, PAUSE_DURATION);
  }, []);

  // Auto-cycle effect. Disabled below `lg`, where Core Capabilities renders as
  // an accordion: a timer that reopens a different section while the visitor is
  // reading (and shifts the page under their thumb) is hostile on mobile.
  useEffect(() => {
    if (!isDesktop || isPaused || isCircleHovered) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setActiveTab(prev => getNextSection(prev));
    }, AUTO_CYCLE_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isDesktop, isPaused, isCircleHovered, getNextSection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, []);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setMousePos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener('mousemove', handleMouseMove);
      return () => section.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  // Map section names to match image naming convention
  const getSectionCode = (section: Section): string => {
    const sectionMap = {
      analysis: "ABA",
      risk: "RM",
      exploit: "EV",
    };
    return sectionMap[section];
  };

  const getSvgPath = (): string => {
    const currentCode = getSectionCode(activeTab);
    if (!hoveredSection || hoveredSection === activeTab) {
      return `/images/Home/${currentCode} - Select.svg`;
    }
    const hoveredCode = getSectionCode(hoveredSection);
    return `/images/Home/${currentCode} - Select - ${hoveredCode} Hovered.svg`;
  };

  const getDegrees = (section: Section) => {
    switch (section) {
      case "analysis":
        return -90; // top (ABA)
      case "exploit":
        return 150; // bottom right (EV)
      case "risk":
        return 30; // bottom left (RM)
    }
  };

  const getSectionTitle = (section: Section): string => {
    const titles = {
      analysis: "Advanced Blackbox Analysis",
      risk: "Risk Management",
      exploit: "Exploit Validation",
    };
    return titles[section];
  };

  // 120° wedge: from top center toward bottom right
  const wedgeClip =
    "polygon(50% 50%, 50% 0%, 93% 25%, 100% 50%, 93% 75%, 50% 100%)";

  // Gutters + spacing match the other four product pages (px-4 sm:px-6 lg:px-8,
  // no top padding — the shared header already supplies the 48px gap under the
  // tabs — and pb-16 before the footer).
  return (
    <section ref={sectionRef} className="relative w-full min-h-[calc(100vh-200px)] flex flex-col items-start justify-start px-4 sm:px-6 lg:px-8 pt-2 pb-16 overflow-hidden">
      {/* Interactive cursor glow */}
      <div 
        className="pointer-events-none absolute w-[350px] h-[350px] rounded-full opacity-0 hover:opacity-20 transition-opacity duration-300 z-[5]"
        style={{
          left: mousePos.x - 175,
          top: mousePos.y - 175,
          background: 'radial-gradient(circle, rgba(236, 25, 56, 0.25) 0%, transparent 70%)',
          filter: 'blur(50px)',
          transition: 'left 0.15s ease-out, top 0.15s ease-out',
        }}
      />
      {/* Animated grid background */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none scarf-grid"
        style={{
          backgroundImage: `
            linear-gradient(rgba(236, 25, 56, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(236, 25, 56, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Hexagonal security pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hex-pattern" width="80" height="69.28" patternUnits="userSpaceOnUse">
              <polygon points="40,0 60,17.32 60,51.96 40,69.28 20,51.96 20,17.32" 
                       fill="none" 
                       stroke="rgba(236, 25, 56, 0.4)" 
                       strokeWidth="0.5"/>
              <circle cx="40" cy="34.64" r="2" fill="rgba(236, 25, 56, 0.6)"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hex-pattern)" className="hex-drift"/>
        </svg>
      </div>

      {/* Supply chain network nodes */}
      <div className="absolute inset-0 pointer-events-none opacity-15 overflow-hidden">
        <svg className="w-full h-full">
          {[...Array(8)].map((_, i) => {
            const x = (i % 4) * 25 + 10;
            const y = Math.floor(i / 4) * 40 + 20;
            return (
              <g key={i}>
                <circle 
                  cx={`${x}%`} 
                  cy={`${y}%`} 
                  r="3" 
                  fill="rgba(236, 25, 56, 0.6)"
                  className="supply-node"
                  style={{ animationDelay: `${i * 0.2}s` }}
                />
                {i < 7 && (
                  <line
                    x1={`${x}%`}
                    y1={`${y}%`}
                    x2={`${((i + 1) % 4) * 25 + 10}%`}
                    y2={`${Math.floor((i + 1) / 4) * 40 + 20}%`}
                    stroke="rgba(236, 25, 56, 0.3)"
                    strokeWidth="1"
                    className="supply-connection"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Scanning hex codes */}
      <ClientOnly>
        <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="hex-code-stream"
              style={{
                top: `${i * 8.33}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${5 + Math.random() * 2}s`
              }}
            >
              SCARF_0x{Math.random().toString(16).substring(2, 10).toUpperCase()}
            </div>
          ))}
        </div>
      </ClientOnly>

      {/* Floating security badges */}
      <ClientOnly>
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="security-badge"
              style={{
                left: `${10 + i * 12}%`,
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${8 + Math.random() * 4}s`
              }}
            >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L12 7L17 8L13.5 11.5L14.5 17L10 14L5.5 17L6.5 11.5L3 8L8 7L10 2Z" 
                    stroke="rgba(236, 25, 56, 0.6)" 
                    strokeWidth="1" 
                    fill="none"/>
            </svg>
            </div>
          ))}
        </div>
      </ClientOnly>

      {/* 1300px content box — same as products/layout.tsx and the other product
          components, so the copy lines up with the page title's red bar. */}
      <div className="max-w-[1300px] w-full mx-auto relative z-10">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 xl:gap-16 items-start mb-16 lg:mb-16">
          {/* Left Side - Description */}
          <div className="space-y-6 md:space-y-8">
             <ScrollReveal direction="up" delay={0}>
            <p className="text-white font-normal text-[14px] sm:text-[15px] lg:text-[16px] leading-[26px] sm:leading-[27px] lg:leading-[28px] tracking-[0.5px]">
              <span className="font-semibold text-primary relative inline-block group/scarf cursor-default">
                <span className="relative z-10">SCARF</span>
                <span className="absolute inset-0 bg-primary/10 scale-0 group-hover/scarf:scale-100 transition-transform duration-300 rounded" />
                <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-primary via-primary/50 to-transparent scale-x-0 group-hover/scarf:scale-x-100 transition-transform duration-500 origin-left" />
              </span> delivers advanced binary supply chain security through
              three core capabilities: <span className="text-primary/80 hover:text-primary transition-colors duration-200 cursor-default font-medium">Advanced Blackbox Analysis</span>, <span className="text-primary/80 hover:text-primary transition-colors duration-200 cursor-default font-medium">Risk
              Management</span>, and <span className="text-primary/80 hover:text-primary transition-colors duration-200 cursor-default font-medium">Exploit Validation</span>. Our platform provides
              comprehensive risk assessment and mitigation for closed-source
              software without requiring source code access
              <span className="text-primary/60 hover:text-primary transition-colors duration-200 cursor-default"> (source-free/binary)</span>.
            </p>
            </ScrollReveal>

            {/* Key Value Proposition */}
            <ScrollReveal direction="up" delay={100}>
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-8 group/heading">
                {/* Below sm the title may wrap: kept on one line it eats the row
                    and leaves the sibling rule only ~35px. */}
                <h2 className="text-white font-bold text-lg sm:text-xl md:text-2xl uppercase tracking-wider whitespace-normal sm:whitespace-nowrap relative cursor-default">
                  <span className="relative z-10 inline-block">
                    {'Key Value Proposition'.split(' ').map((word, i, arr) => (
                      <span 
                        key={i}
                        className="inline-block hover:text-primary transition-all duration-300 hover:scale-110"
                        style={{ 
                          transitionDelay: `${i * 50}ms`,
                          marginRight: i < arr.length - 1 ? '0.25em' : '0',
                        }}
                      >
                        {word}
                      </span>
                    ))}
                  </span>
                  <span className="absolute -left-2 top-0 bottom-0 w-1 bg-primary scale-y-0 group-hover/heading:scale-y-100 transition-transform duration-500 origin-top" />
                </h2>
              <div className="flex-1 h-[1px] bg-white/10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover/heading:opacity-100 group-hover/heading:animate-scan-line-heading" />
              </div>
            </div>

              {keyValueItems.map((section, index) => {
                const isHovered = hoveredValueIndex === index;
                
                return (
                  <div
                    key={index}
                    className="space-y-3 group relative pl-4 transition-all duration-300"
                    onMouseEnter={() => setHoveredValueIndex(index)}
                    onMouseLeave={() => setHoveredValueIndex(null)}
                  >
                    {/* Left border with glow effect */}
                    <div className={`absolute left-0 top-0 bottom-0 w-[2px] transition-all duration-500 ${
                      isHovered 
                        ? 'bg-primary shadow-[0_0_15px_rgba(236,25,56,0.6)]' 
                        : 'bg-white/10'
                    }`} />
                    
                    <h3 className={`font-semibold text-[16px] leading-[24px] tracking-[0.5px] transition-all duration-300 ${
                      isHovered ? 'text-primary translate-x-2' : 'text-primary/80'
                    }`}>
                      {section.title}
                    </h3>
                    <ul className="space-y-2">
                      {section.items.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          className={`flex items-start gap-3 text-white font-normal text-[14px] sm:text-[15px] lg:text-[16px] leading-[26px] sm:leading-[27px] lg:leading-[28px] tracking-[0.5px] transition-all duration-300 group/item cursor-default relative ${
                            isHovered ? 'translate-x-1' : ''
                          }`}
                          style={{
                            transitionDelay: isHovered ? `${itemIndex * 50}ms` : '0ms',
                          }}
                        >
                          {/* Animated bullet with ring effect */}
                          <span className="relative mt-1 flex-shrink-0">
                            <span className={`transition-all duration-300 inline-block ${
                              isHovered ? 'text-primary scale-125 rotate-180' : 'text-primary/60'
                            } group-hover/item:scale-150 group-hover/item:rotate-90`}>
                              ●
                            </span>
                            <span className="absolute inset-0 text-primary opacity-0 group-hover/item:opacity-100 group-hover/item:animate-ping">
                              ●
                            </span>
                          </span>
                          
                          {/* Text with hover effects */}
                          <span className={`relative flex-1 transition-all duration-300 ${
                            isHovered ? 'text-white' : 'text-white/80'
                          } group-hover/item:translate-x-1`}>
                            <span className="relative z-10">
                              {item.split(':').map((part, i) => (
                                <span key={i} className={i === 0 ? 'font-semibold group-hover/item:text-primary transition-colors duration-200' : ''}>
                                  {part}{i === 0 && item.includes(':') ? ':' : ''}
                                </span>
                              ))}
                            </span>
                            <span className="absolute left-0 bottom-0 w-0 h-[1px] bg-primary/20 group-hover/item:w-full transition-all duration-500" />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            </ScrollReveal>
          </div>

          {/* Right Side - Interactive Image */}
          <ScrollReveal direction="up" delay={200}>
          <div className="flex items-center justify-center lg:justify-end pt-6 lg:pt-0 pb-20 lg:pb-0">
            <div
              className="relative w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[360px] md:h-[360px] lg:w-[380px] lg:h-[380px] xl:w-[420px] xl:h-[420px]"
              onMouseEnter={() => setIsCircleHovered(true)}
              onMouseLeave={() => setIsCircleHovered(false)}
            >
              {/* Animated glow behind */}
              <div className={`absolute inset-0 rounded-full blur-3xl transition-all duration-700 ${
                isCircleHovered
                  ? 'bg-primary/20 scale-110'
                  : 'bg-primary/5 scale-100'
              }`} />

              {/* Secondary glow ring on hover */}
              <div className={`absolute inset-[-20px] rounded-full border transition-all duration-500 ${
                isCircleHovered
                  ? 'border-primary/30 opacity-100'
                  : 'border-transparent opacity-0'
              }`} />

              {/* Tertiary decorative ring - hidden on mobile */}
              <div
                className={`hidden sm:block absolute inset-[-35px] rounded-full border border-dashed transition-all duration-700 ${
                  isCircleHovered
                    ? 'border-primary/20 opacity-100'
                    : 'border-white/5 opacity-50'
                }`}
                style={{
                  animation: isCircleHovered ? 'none' : 'spin 40s linear infinite reverse',
                }}
              />

              {/* Rotating Outer Ring */}
              <div
                className="absolute inset-0 pointer-events-none transition-all duration-700"
                style={{
                  animation: isCircleHovered ? 'none' : 'spin 20s linear infinite',
                  transform: isCircleHovered ? 'scale(1.03)' : 'scale(1)',
                }}
              >
                <Image
                  src="/images/Home/Scarf_circle.svg"
                  alt="Rotating ring"
                  fill
                  className={`object-contain transition-all duration-500 ${
                    isCircleHovered ? 'opacity-100 drop-shadow-[0_0_20px_rgba(236,25,56,0.4)]' : 'opacity-80'
                  }`}
                />
              </div>

              {/* Decorative outer arc segments (Figma) — counter-rotates for radar feel */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  animation: isCircleHovered ? 'none' : 'spin 30s linear infinite reverse',
                }}
              >
                <Image
                  src="/images/Product/scarf-ring-overlay.svg"
                  alt=""
                  fill
                  className={`object-contain transition-opacity duration-500 ${
                    isCircleHovered ? 'opacity-100' : 'opacity-90'
                  }`}
                />
              </div>

              {/* Interactive Center with Sections */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`relative w-[70%] aspect-square rounded-full overflow-hidden transition-transform duration-500 ${
                  isCircleHovered ? 'scale-105' : 'scale-100'
                }`}>
                  <Image
                    src={getSvgPath()}
                    alt="SCARF Core"
                    fill
                    className="object-contain transition-all duration-300 pointer-events-none"
                  />

                  {/* Clickable 3 wedges */}
                  {(["analysis", "risk", "exploit"] as Section[]).map(
                    (section) => {
                      const deg = getDegrees(section);

                      return (
                        <div
                          key={section}
                          className="absolute inset-0 pointer-events-auto transition-all duration-200"
                          style={{
                            transform: `rotate(${deg}deg)`,
                            transformOrigin: "50% 50%",
                            clipPath: wedgeClip,
                            WebkitClipPath: wedgeClip,
                            cursor: "pointer",
                          }}
                          onClick={() => handleSectionClick(section)}
                          onMouseEnter={() => setHoveredSection(section)}
                          onMouseLeave={() => setHoveredSection(null)}
                        />
                      );
                    }
                  )}
                </div>
              </div>

              {/* Active section label - adjusted for mobile */}
              <div className={`absolute -bottom-20 sm:-bottom-16 left-1/2 -translate-x-1/2 text-center transition-all duration-500 ${
                isCircleHovered ? 'opacity-100 translate-y-0' : 'opacity-70 translate-y-1'
              }`}>
                <div className="text-primary text-xs sm:text-sm font-medium whitespace-nowrap px-3 py-1 bg-primary/10 rounded-full border border-primary/20">
                  {getSectionTitle(activeTab)}
                </div>
              </div>

              {/* Cycle indicator dots - adjusted for mobile */}
              <div className="absolute -bottom-10 sm:-bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {sectionKeys.map((section, idx) => (
                  <button
                    key={section}
                    onClick={() => handleSectionClick(section)}
                    className={`relative h-2 rounded-full transition-all duration-500 ease-out cursor-pointer group/dot ${
                      activeTab === section
                        ? 'bg-primary w-6 shadow-[0_0_10px_rgba(236,25,56,0.5)]'
                        : 'bg-white/30 w-2 hover:bg-white/50 hover:w-4'
                    }`}
                    aria-label={`Select ${getSectionTitle(section)}`}
                  >
                    {/* Interactive ring on hover */}
                    <span className={`absolute inset-0 rounded-full border-2 border-primary opacity-0 group-hover/dot:opacity-100 scale-100 group-hover/dot:scale-150 transition-all duration-300 ${
                      activeTab === section ? 'animate-ping' : ''
                    }`} />
                    
                    {/* Tooltip label on hover */}
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-primary font-mono opacity-0 group-hover/dot:opacity-100 transition-opacity duration-200 whitespace-nowrap px-2 py-1 bg-black/50 rounded backdrop-blur-sm">
                      {getSectionTitle(section).split(' ')[0]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          </ScrollReveal>
        </div>

        {/* Core Capabilities Section */}
        <ScrollReveal direction="up" delay={300}>
        <div className="space-y-6">
          <div className="flex items-center gap-4 mb-8 group/heading2">
              {/* Same wrap allowance as the Key Value Proposition heading. */}
              <h2 className="text-white font-bold text-lg sm:text-xl md:text-2xl uppercase tracking-wider whitespace-normal sm:whitespace-nowrap relative cursor-default">
                <span className="relative z-10">
                  {'Core Capabilities'.split(' ').map((word, i, arr) => (
                    <span 
                      key={i}
                      className="inline-block hover:text-primary transition-all duration-300 hover:scale-110"
                      style={{ 
                        transitionDelay: `${i * 50}ms`,
                        marginRight: i < arr.length - 1 ? '0.25em' : '0',
                      }}
                    >
                      {word}
                    </span>
                  ))}
                </span>
                <span className="absolute -left-2 top-0 bottom-0 w-1 bg-primary scale-y-0 group-hover/heading2:scale-y-100 transition-transform duration-500 origin-top" />
              </h2>
              <div className="flex-1 h-[1px] bg-white/10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover/heading2:opacity-100 group-hover/heading2:animate-scan-line-heading" />
              </div>
            </div>
          <div className="bg-background relative">
            <TabbedContent
              items={coreCapabilities}
              defaultTab={activeTab}
              currentTab={activeTab}
              onTabChange={(id) => handleSectionClick(id as Section)}
            />
          </div>
        </div>
        </ScrollReveal>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        .scarf-grid {
          animation: gridMove 30s linear infinite;
        }

        .scarf-particle {
          animation: float 4s ease-in-out infinite;
        }

        /* Hexagon Drift */
        .hex-drift {
          animation: hex-move 20s linear infinite;
        }
        
        @keyframes hex-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(80px, 69.28px); }
        }

        /* Supply Chain Nodes */
        .supply-node {
          animation: node-pulse 2s ease-in-out infinite;
        }
        
        @keyframes node-pulse {
          0%, 100% {
            r: 3;
            opacity: 0.6;
          }
          50% {
            r: 5;
            opacity: 1;
          }
        }

        /* Supply Chain Connections */
        .supply-connection {
          stroke-dasharray: 200;
          stroke-dashoffset: 200;
          animation: draw-connection 3s ease-in-out infinite;
        }
        
        @keyframes draw-connection {
          0% {
            stroke-dashoffset: 200;
            opacity: 0;
          }
          50% {
            stroke-dashoffset: 0;
            opacity: 1;
          }
          100% {
            stroke-dashoffset: -200;
            opacity: 0;
          }
        }

        /* Hex Code Stream */
        .hex-code-stream {
          position: absolute;
          left: -30%;
          font-family: 'Courier New', monospace;
          font-size: 11px;
          color: rgba(236, 25, 56, 0.7);
          animation: hex-stream linear infinite;
          white-space: nowrap;
          letter-spacing: 2px;
        }
        
        @keyframes hex-stream {
          0% { left: -30%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 130%; opacity: 0; }
        }

        /* Security Badge Float */
        .security-badge {
          position: absolute;
          animation: badge-float ease-in-out infinite;
        }
        
        @keyframes badge-float {
          0%, 100% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          50% {
            transform: translateY(-50vh) rotate(180deg);
            opacity: 0.8;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes gridMove {
          0% { background-position: 0 0; }
          100% { background-position: 60px 60px; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
          50% { transform: translateY(-20px) scale(1.2); opacity: 0.6; }
        }
        
        /* Heading Scan Line */
        @keyframes scan-line-heading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        
        .animate-scan-line-heading {
          animation: scan-line-heading 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}